/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService } from './api.service';
import { AuthStoreService } from './authStore.service';
import { ErrorResponseModel } from '../../models';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { ModulesEnum } from '../../enums';
import { Observable } from 'rxjs';
import { TransactionsEnum } from '../../../modules/loadbalancer/shared/enums/api';

@Injectable()
export class TransactionService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
        private logService: LogService,
    ) {
    }

    /**
     * Start a transaction
     * Note: used to create a series of pending changes via the API
     */
    public startTransaction(options?: any): Observable<any> {
        const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

        return new Observable((observer) => {
                this.apiService.put(TransactionsEnum.PUT_TRANSACTION_START, {}, moduleAuth)
                    .subscribe({
                        next: (): void => {
                            this.logService.debug('ApiService:startTransaction()', 'Transaction system started');
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('ApiService:startTransaction()', 'Transaction system failed to start', error);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Commit pending server API changes
     */
    public commitTransaction(options?: any): Observable<any> {
        const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

        return new Observable((observer) => {
                this.apiService.put(TransactionsEnum.PUT_TRANSACTION_COMMIT, {}, moduleAuth, false, { timeout: 20000 })
                    .subscribe({
                        next: (): void => {
                            this.logService.debug('ApiService:commitTransaction()', 'Transaction system committed');
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('ApiService:startTransaction()', 'Transaction system failed to commit', error);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Reverse pending server API changes
     */
    public rollbackTransaction(options?: any): Observable<any> {
        const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

        return new Observable((observer) => {
                this.apiService.put(TransactionsEnum.PUT_TRANSACTION_ROLLBACK, {}, moduleAuth)
                    .subscribe({
                        next: (): void => {
                            this.logService.debug('ApiService:commitTransaction()', 'Transactions rolled back');
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('ApiService:startTransaction()', 'Transaction system failed to roll back', error);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }
}
